import React, { useMemo } from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Anti-bot: encode contact info so crawlers can't scrape it
const ob = (s) => s.split('').map(c => `&#${c.charCodeAt(0)};`).join('');
const ObfEmail = ({ e, label }) => {
  const href = useMemo(() => `mailto:${e}`, [e]);
  const encoded = useMemo(() => ob(e), [e]);
  return <a href={href} className="text-sky-500 hover:underline" dangerouslySetInnerHTML={{ __html: label || encoded }} />;
};
const ObfPhone = ({ n, label }) => {
  const href = useMemo(() => `tel:${n.replace(/\s/g, '')}`, [n]);
  const encoded = useMemo(() => ob(label || n), [n, label]);
  return <a href={href} className="text-sky-500 hover:underline" dangerouslySetInnerHTML={{ __html: encoded }} />;
};
const ObfText = ({ t }) => {
  const encoded = useMemo(() => ob(t), [t]);
  return <span dangerouslySetInnerHTML={{ __html: encoded }} />;
};

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-8">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-sky-600 mb-6 transition-colors"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Torna indietro
        </button>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-sky-500" />
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Informativa sulla Privacy</h1>
          </div>

          <div className="prose prose-sm prose-slate max-w-none space-y-6 text-slate-700 text-sm leading-relaxed">
            <p className="text-xs text-slate-400">Ultimo aggiornamento: Luglio 2026</p>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">1. Titolare del Trattamento</h2>
              <p>
                Il titolare del trattamento dei dati personali e' <strong>TELCAVOIP INTERNATIONAL EU</strong>, 
                con sede legale in Ul. Zbozowa 4A, 70-653 Szczecin (Polonia).
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-3 text-xs space-y-1">
                <p><strong>Sede legale:</strong> Ul. Zbozowa 4A, 70-653 Szczecin, Polonia</p>
                <p><strong>P.IVA:</strong> <ObfText t="PL8513242072" /></p>
                <p><strong>Email Privacy:</strong> <ObfEmail e="privacy@telcavoip.eu" /></p>
                <p><strong>Email Assistenza:</strong> <ObfEmail e="support@telcavoip.eu" /></p>
                <p><strong>Telefono Italia:</strong> <ObfPhone n="+39 0427 881500" /></p>
                <p><strong>Telefono Polonia:</strong> <ObfPhone n="+48 573 187804" /></p>
              </div>
              <p className="mt-3">
                Il Titolare non ha nominato un Responsabile della Protezione dei Dati (DPO) in quanto non ricorrono le condizioni di cui all'Art. 37 del GDPR.
                Per qualsiasi richiesta relativa alla privacy, contattare: <ObfEmail e="privacy@telcavoip.eu" />.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">2. Dati Raccolti</h2>
              <p>La piattaforma VoiceBook raccoglie e tratta le seguenti categorie di dati:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong>Dati identificativi:</strong> nome, cognome, email, numero di telefono, ragione sociale, P.IVA (per i Partner)</li>
                <li><strong>Dati sanitari:</strong> appuntamenti medici, reparti, medici di riferimento, ricette mediche (dati trattati come categoria particolare ai sensi dell'Art. 9 GDPR)</li>
                <li><strong>Dati di navigazione:</strong> log tecnici, indirizzi IP, informazioni sul dispositivo</li>
                <li><strong>Registrazioni vocali:</strong> trascrizioni delle chiamate telefoniche gestite dall'AI. Le chiamate vengono trascritte in tempo reale per l'erogazione del servizio; le registrazioni audio non vengono conservate, solo le trascrizioni testuali.</li>
                <li><strong>Dati di fatturazione:</strong> storico ricariche wallet, transazioni pagamento, consumi traffico voce</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">3. Finalita' e Base Giuridica del Trattamento</h2>
              <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden mt-2">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-600 border-b">Finalita'</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600 border-b">Base Giuridica</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600 border-b">Riferimento</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="px-3 py-2">Erogazione del servizio di prenotazione</td>
                    <td className="px-3 py-2">Esecuzione contrattuale</td>
                    <td className="px-3 py-2">Art. 6.1.b</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-3 py-2">Trattamento dati sanitari (appuntamenti, ricette)</td>
                    <td className="px-3 py-2">Consenso esplicito del paziente</td>
                    <td className="px-3 py-2">Art. 9.2.a</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-3 py-2">Trascrizione chiamate vocali AI</td>
                    <td className="px-3 py-2">Esecuzione contrattuale + legittimo interesse</td>
                    <td className="px-3 py-2">Art. 6.1.b/f</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-3 py-2">Invio reminder e conferme appuntamenti</td>
                    <td className="px-3 py-2">Legittimo interesse</td>
                    <td className="px-3 py-2">Art. 6.1.f</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-3 py-2">Fatturazione e gestione abbonamenti</td>
                    <td className="px-3 py-2">Obbligo legale</td>
                    <td className="px-3 py-2">Art. 6.1.c</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Miglioramento del servizio AI</td>
                    <td className="px-3 py-2">Legittimo interesse</td>
                    <td className="px-3 py-2">Art. 6.1.f</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">4. Consenso per le Registrazioni Vocali</h2>
              <p>
                Quando un paziente chiama il numero della clinica gestito da VoiceBook, viene informato che la chiamata e' gestita da un assistente AI 
                e che la conversazione verra' trascritta per finalita' di erogazione del servizio. Il paziente puo' richiedere in qualsiasi momento 
                di parlare con un operatore umano. Le trascrizioni sono accessibili solo al personale autorizzato della clinica.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">5. Conservazione dei Dati</h2>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Dati degli appuntamenti: <strong>24 mesi</strong> dalla data dell'appuntamento</li>
                <li>Trascrizioni vocali: <strong>12 mesi</strong></li>
                <li>Dati di fatturazione: <strong>10 anni</strong> (normativa fiscale)</li>
                <li>Dati account utente: fino alla cancellazione dell'account</li>
                <li>Dati dei pazienti: fino alla richiesta di cancellazione o <strong>36 mesi</strong> dall'ultima interazione</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">6. Condivisione dei Dati e Sub-Responsabili</h2>
              <p>I dati personali possono essere condivisi con:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong>Centri medici (cliniche):</strong> per l'erogazione del servizio di prenotazione — in qualita' di Responsabili del Trattamento (Art. 28 GDPR)</li>
                <li><strong>Fornitori di servizi di pagamento</strong> — per l'elaborazione sicura delle transazioni</li>
                <li><strong>Fornitori di tecnologia AI</strong> — per l'elaborazione vocale e la trascrizione, con server in UE/USA</li>
                <li><strong>TelcaVoIP</strong> — infrastruttura telefonica VoIP</li>
                <li><strong>Fornitori di servizi email</strong> — per l'invio di email transazionali (conferme, reminder)</li>
              </ul>
              <p className="mt-2">Tutti i sub-responsabili hanno sottoscritto clausole contrattuali conformi al GDPR (Art. 28). Non vendiamo ne' cediamo dati personali a terzi per finalita' di marketing.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">7. Accordo sul Trattamento dei Dati (DPA)</h2>
              <p>
                Per i Partner (clienti B2B) che utilizzano VoiceBook per gestire le proprie cliniche, e' disponibile un 
                <strong> Data Processing Agreement (DPA)</strong> conforme all'Art. 28 del GDPR che regola il trattamento dei dati dei pazienti.
                Per richiederlo: <ObfEmail e="privacy@telcavoip.eu" />.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">8. Trasferimenti Extra-UE</h2>
              <p>
                Alcuni dei nostri sub-responsabili hanno sede negli Stati Uniti. 
                I trasferimenti avvengono sulla base delle <strong>Standard Contractual Clauses (SCC)</strong> approvate 
                dalla Commissione Europea (Decisione 2021/914) e del <strong>EU-US Data Privacy Framework</strong>, ove applicabile.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">9. Diritti dell'Interessato</h2>
              <p>Ai sensi degli Artt. 15-22 del GDPR, hai il diritto di:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong>Accesso</strong> (Art. 15): ottenere copia dei tuoi dati personali</li>
                <li><strong>Rettifica</strong> (Art. 16): correggere dati inesatti o incompleti</li>
                <li><strong>Cancellazione</strong> (Art. 17): richiedere la cancellazione dei tuoi dati ("diritto all'oblio")</li>
                <li><strong>Limitazione</strong> (Art. 18): limitare il trattamento in determinate circostanze</li>
                <li><strong>Portabilita'</strong> (Art. 20): ricevere i tuoi dati in formato strutturato e leggibile da dispositivo automatico</li>
                <li><strong>Opposizione</strong> (Art. 21): opporti al trattamento basato sul legittimo interesse</li>
                <li><strong>Revoca del consenso</strong> (Art. 7): in qualsiasi momento, senza pregiudicare la liceita' del trattamento basato sul consenso prestato prima della revoca</li>
              </ul>
              <p className="mt-2">
                Per esercitare i tuoi diritti, invia una richiesta a: <ObfEmail e="privacy@telcavoip.eu" />. Risponderemo entro 30 giorni dalla ricezione.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">10. Cookie</h2>
              <p>
                La piattaforma utilizza esclusivamente <strong>cookie tecnici</strong> necessari al funzionamento 
                (autenticazione, sessione, preferenze lingua). Non utilizziamo cookie di profilazione, 
                analytics di terze parti, ne' cookie pubblicitari.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">11. Sicurezza</h2>
              <p>
                Adottiamo misure tecniche e organizzative appropriate per proteggere i dati personali, tra cui:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Crittografia dei dati in transito (TLS 1.3 / HTTPS)</li>
                <li>Hashing delle password (bcrypt con salt)</li>
                <li>Accesso basato su ruoli (RBAC) con 5 livelli di autorizzazione</li>
                <li>Audit log delle operazioni sensibili</li>
                <li>Autenticazione OTP per accesso ai dati sanitari sensibili (ricette mediche)</li>
                <li>Backup regolari dei dati</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">12. Autorita' di Controllo</h2>
              <p>
                Hai il diritto di proporre reclamo all'autorita' di controllo competente:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong>Italia:</strong> Garante per la Protezione dei Dati Personali — <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline">garanteprivacy.it</a></li>
                <li><strong>Polonia:</strong> UODO (Urz&#261;d Ochrony Danych Osobowych) — <a href="https://uodo.gov.pl" target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline">uodo.gov.pl</a></li>
              </ul>
            </section>

            <div className="mt-8 pt-6 border-t border-slate-200 text-center space-y-1">
              <p className="text-xs text-slate-500"><strong>TELCAVOIP INTERNATIONAL EU</strong></p>
              <p className="text-xs text-slate-400">Ul. Zbozowa 4A, 70-653 Szczecin (PL)</p>
              <p className="text-xs text-slate-400">
                <ObfEmail e="support@telcavoip.eu" /> | <ObfPhone n="+39 0427 881500" />
              </p>
              <a href="https://telcavoip.ai" target="_blank" rel="noopener noreferrer" className="text-sky-500 text-xs hover:underline">
                telcavoip.ai
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
